import type { Address, Address as ViemAddress } from "viem"
import { getAddress, isAddress } from "viem"

/**
 * Lowercase Address value object that ensures consistent address handling across the application.
 *
 * Features:
 * - Automatic lowercase normalization
 * - Built-in validation
 * - Checksum support
 * - Rich domain behavior
 * - Web3 library abstraction
 *
 * @example
 * ```typescript
 * const userAddress = new LcAddress("0xABC123...")
 * const tokenAddress = new LcAddress("0xabc123...")
 *
 * // Safe comparison
 * if (userAddress.equals(tokenAddress)) {
 *   // Logic here
 * }
 *
 * // Rich behavior
 * console.log(userAddress.truncate()) // "0xABC1...3456"
 * console.log(userAddress.toChecksum()) // "0xAbC123..."
 * ```
 */
export class LcAddress {
  private readonly value: string

  constructor(input: string | ViemAddress) {
    const addressString = String(input)

    if (!isAddress(addressString)) {
      throw new Error(`Invalid address: ${addressString}`)
    }

    // Always store in lowercase for consistent comparison
    this.value = addressString.toLowerCase()
  }

  static fromPortalsAddress(input: string): LcAddress {
    const parts = input.split(":")
    if (parts.length > 1) {
      return new LcAddress(parts?.[1] ?? input)
    }
    return new LcAddress(input)
  }

  /**
   * Get the lowercase address string
   */
  toString(): string {
    return this.value
  }

  /**
   * Get the lowercase address string
   */
  toViemAddress(): Address {
    return this.value as Address
  }

  /**
   * Convert the address to a portals address
   *
   * @param chainId - The chainId to convert the address to
   * @returns
   */
  toPortalsAddress(chainId: number) {
    if (chainId === 8453) return `base:${this.value}`
    throw new Error(`Unsupported chainId: ${chainId}`)
  }

  /**
   * Get the checksummed address (mixed case)
   */
  toChecksum(): ViemAddress {
    return getAddress(this.value as ViemAddress)
  }

  /**
   * Check if this address equals another address
   */
  equals(other: LcAddress): boolean {
    return this.value === other.value
  }

  /**
   * Check if this address equals a string address
   */
  equalsString(other: string): boolean {
    try {
      const otherAddress = new LcAddress(other)
      return this.equals(otherAddress)
    } catch {
      return false
    }
  }

  includes(other: string): boolean {
    return this.value.includes(other.toLowerCase())
  }

  /**
   * Check if this is the zero address
   */
  isZero(): boolean {
    return this.value === "0x0000000000000000000000000000000000000000"
  }

  /**
   * Truncate the address for display purposes
   */
  truncate(start = 6, end = 4): string {
    if (start + end >= this.value.length) {
      return this.value
    }
    return `${this.value.slice(0, start)}...${this.value.slice(-end)}`
  }

  /**
   * Validate that a string is a valid address
   */
  static isValid(input: string): boolean {
    return isAddress(input)
  }

  /**
   * Create an LcAddress from a string, returning null if invalid
   */
  static tryCreate(input: string): LcAddress | null {
    try {
      return new LcAddress(input)
    } catch {
      return null
    }
  }

  /**
   * Compare two addresses for sorting
   */
  static compare(a: LcAddress, b: LcAddress): number {
    return a.value.localeCompare(b.value)
  }

  /**
   * The zero address constant
   */
  static readonly ZERO = new LcAddress("0x0000000000000000000000000000000000000000")

  /**
   * Get the raw string value (for compatibility with viem)
   */
  get raw(): ViemAddress {
    return this.value as ViemAddress
  }

  /**
   * JSON serialization
   */
  toJSON(): string {
    return this.value
  }

  /**
   * Value object equality for debugging
   */
  valueOf(): string {
    return this.value
  }
}
